import React, { useEffect, useState } from "react";
import { CheckCircle2, Copy, Download, Loader2, Mail, RotateCcw, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { Card } from "@/components/admin/ui";

const TITLES: Record<string, [string, string]> = {
  certificates: ["Certificates to Compliance Coordinators", "Used by Open in my email and Send from portal. Portal emails come from Info Retrieval Services and replies come to you."],
  lists: ["Send lists (all cleared & expiring)", "Sent when you click Send lists on a charter. Replies come to you."],
  expiring: ["Upcoming expirations notice (to you)", "Sent to you each morning when clearances newly fall within 90 days of expiring. From the portal."],
};

export function CertificateQueue({ refresh }: { refresh: number }) {
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/dow/certificates/pending").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [refresh]);
  const [opened, setOpened] = useState<string[]>([]);
  const [mailOn, setMailOn] = useState(false);
  useEffect(() => { api.get("/dow/prefs").then((r) => setMailOn(r.data.open_in_email)).catch(() => {}); }, [refresh]);
  const download = async (r: any) => {
    setBusy(`p${r.charter}`);
    try { await downloadFile(`/dow/certificates/download?charter=${encodeURIComponent(r.charter)}`, `Charter-${r.charter || "unmatched"}-Certificates.pdf`); setOpened((o) => [...o, r.charter]); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const copy = async (charter: string) => {
    setBusy(`c${charter}`);
    try {
      const { data } = await api.post("/dow/certificates/draft", { charter, link: false });
      await navigator.clipboard.writeText(`To: ${data.to}\nSubject: ${data.subject}\n\n${data.body}`);
      toast.success("Message copied. Paste it into a new email and attach the PDF you downloaded.");
      setOpened((o) => [...o, charter]);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const draft = async (charter: string) => {
    setBusy(`d${charter}`);
    try {
      const { data } = await api.post("/dow/certificates/draft", { charter });
      window.location.href = `mailto:${encodeURIComponent(data.to)}?subject=${encodeURIComponent(data.subject)}&body=${encodeURIComponent(data.body)}`;
      setOpened((o) => [...o, charter]);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const markSent = async (charter: string) => {
    try { const { data } = await api.post("/dow/certificates/mark-sent", { charter }); toast.success(`${data.count} certificate(s) marked as sent`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const send = async (charter: string) => {
    setBusy(charter);
    try {
      const { data } = await api.post("/dow/certificates/send", { charter });
      if (data.sent.length) toast.success(`Sent ${data.sent.reduce((n: number, s: any) => n + s.count, 0)} certificate(s) to ${data.sent.length} Compliance Coordinator(s)`);
      data.problems.forEach((p: any) => toast.warning(`Charter ${p.charter || "?"}: not sent (${p.reason}). Fix the Charter directory, then send again.`));
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  if (!rows || !rows.length) return null;
  return (
    <Card title={`Certificates waiting to go to Compliance Coordinators (${rows.reduce((n, r) => n + r.count, 0)})`} testId="dow-cert-queue-card">
      <p className="px-6 pt-4 text-sm text-[#475569]">Nothing is sent until you choose. To send it yourself: <b>Download PDF</b> (all of that charter's certificates in one file), <b>Copy message</b> (the coordinator's email, subject and your wording), paste into a new email in your own email, attach the PDF and send. Then click <b>Mark as sent</b>. Or click <b>Send from portal</b> and we send it for you, with replies coming to you.</p>
      <div className="divide-y divide-[#E2E8F0] p-2">
        {rows.map((r) => (
          <div key={r.charter} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`dow-cert-queue-${r.charter || "none"}`}>
            <div className="text-sm">
              <p className="font-semibold text-[#1A2536]">{r.charter_name} {r.charter && <span className="text-[#64748B]">(Charter {r.charter})</span>} · {r.count} certificate(s)</p>
              <p className="text-[#64748B]">{r.coordinator_email ? <>To: {r.coordinator_email}</> : <span className="font-semibold text-[#B45309]">{r.charter ? "No Compliance Coordinator email. Add one in the Charter directory." : "This parish/school isn't in the Charter directory. Download these certificates from the Approved list and send them yourself."}</span>}</p>
              <p className="text-xs text-[#94A3B8]">{r.names.join(", ")}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="btn-primary btn-sm" disabled={!!busy} onClick={() => download(r)} data-testid={`dow-cert-download-${r.charter || "none"}`}>
                {busy === `p${r.charter}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF</button>
              {r.charter && <button className="btn-secondary btn-sm" disabled={!!busy} onClick={() => copy(r.charter)} data-testid={`dow-cert-copy-${r.charter}`}>
                {busy === `c${r.charter}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />} Copy message</button>}
              {mailOn && <button className="btn-secondary btn-sm" disabled={!!busy || !r.coordinator_email} onClick={() => draft(r.charter)} data-testid={`dow-cert-draft-${r.charter || "none"}`}>
                {busy === `d${r.charter}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Open in my email</button>}
              <button className="btn-secondary btn-sm" onClick={() => markSent(r.charter)} data-testid={`dow-cert-mark-sent-${r.charter || "none"}`}><CheckCircle2 className="h-4 w-4" /> Mark as sent</button>
              <button className="btn-secondary btn-sm" disabled={!!busy || !r.coordinator_email} onClick={() => send(r.charter)} data-testid={`dow-cert-send-${r.charter || "none"}`}>
                {busy === r.charter ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send from portal</button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function TemplateEditor({ t, onSaved }: { t: any; onSaved: () => void }) {
  const [subject, setSubject] = useState(t.subject);
  const [body, setBody] = useState(t.body);
  const [busy, setBusy] = useState(false);
  const dirty = subject !== t.subject || body !== t.body;
  const save = async () => {
    setBusy(true);
    try { await api.put(`/dow/email-templates/${t.kind}`, { subject, body }); toast.success("Email wording saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const reset = async () => {
    try { await api.delete(`/dow/email-templates/${t.kind}`); setSubject(t.default.subject); setBody(t.default.body); toast.success("Back to the original wording"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3 px-6 py-5" data-testid={`dow-template-${t.kind}`}>
      <div><p className="font-semibold text-[#1A2536]">{TITLES[t.kind][0]}</p><p className="text-xs text-[#64748B]">{TITLES[t.kind][1]}</p></div>
      <label className="block"><span className="field-label">Subject</span>
        <input className="field-input" value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} data-testid={`dow-template-${t.kind}-subject`} /></label>
      <label className="block"><span className="field-label">Message (a blank line starts a new paragraph)</span>
        <textarea className="field-input h-auto py-3 text-sm" rows={7} value={body} maxLength={5000} onChange={(e) => setBody(e.target.value)} data-testid={`dow-template-${t.kind}-body`} /></label>
      <p className="text-xs text-[#64748B]">Fill-ins you can use: {t.fields.map((f: string) => `{${f}}`).join("  ")}. The greeting, download button or list, and Diocese footer are added automatically.</p>
      <div className="flex gap-2">
        <button className="btn-primary btn-sm" disabled={busy || !dirty} onClick={save} data-testid={`dow-template-${t.kind}-save`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save wording</button>
        <button className="btn-secondary btn-sm" onClick={reset} data-testid={`dow-template-${t.kind}-reset`}><RotateCcw className="h-4 w-4" /> Use original wording</button>
      </div>
    </div>
  );
}

export function EmailWording() {
  const [list, setList] = useState<any[] | null>(null);
  const [mailOn, setMailOn] = useState(false);
  const load = () => api.get("/dow/email-templates").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); api.get("/dow/prefs").then((r) => setMailOn(r.data.open_in_email)).catch(() => {}); }, []);
  const toggle = async (v: boolean) => {
    setMailOn(v);
    try { await api.put("/dow/prefs", { open_in_email: v }); toast.success(v ? "“Open in my email” button turned on" : "“Open in my email” button turned off"); } catch (e) { setMailOn(!v); toast.error(errMsg(e)); }
  };
  return (
    <Card title="Email wording" testId="dow-email-wording-card">
      <label className="flex items-start gap-3 border-b border-[#E2E8F0] px-6 py-4 text-sm">
        <input type="checkbox" className="mt-0.5 h-5 w-5 accent-[#1B365D]" checked={mailOn} onChange={(e) => toggle(e.target.checked)} data-testid="dow-pref-open-in-email" />
        <span><b>Show an “Open in my email” button</b><br /><span className="text-[#64748B]">Optional. Opens a ready-made message in the email program set up on your computer (for example Outlook), with a download link instead of an attachment. Leave off if you use webmail or prefer Download PDF and Copy message.</span></span>
      </label>
      {list === null ? <div className="p-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-[#94A3B8]" /></div>
        : <div className="divide-y divide-[#E2E8F0]">{list.map((t) => <TemplateEditor key={t.kind + t.subject + t.body} t={t} onSaved={load} />)}</div>}
    </Card>
  );
}
